import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeliveryStatusResponse } from "@gull/shared";
import { ApiError, enduserApi } from "../../../lib/api";
import { useAuth } from "../../../lib/auth";

function statusPath(referenceNumber: string | null, token: string | null) {
  const reference = referenceNumber ? encodeURIComponent(referenceNumber) : "";
  const query = token ? `?token=${encodeURIComponent(token)}` : "";
  return `/deliveries/status/${reference}${query}`;
}

/**
 * The delivery status behind both customer URL shapes. One request answers everything: the
 * reference identifies the delivery, the token decides whether the page may act, and the server
 * says which by returning `approval` — so there is nothing here for the caller to reconcile.
 */
export function useDeliveryStatus(referenceNumber: string | null, token: string | null) {
  const queryClient = useQueryClient();
  // The signed-in reader is part of the key: the server tells the delivery's own customer more
  // (where a payout would go), so signing in or out is a different answer, not a stale one.
  const { user, loading: authLoading } = useAuth();
  const queryKey = ["delivery-status", referenceNumber, token, user?.uid ?? null] as const;

  const query = useQuery({
    queryKey,
    enabled: !authLoading && !!(referenceNumber || token),
    queryFn: () =>
      user
        ? enduserApi.authedGet<DeliveryStatusResponse>(statusPath(referenceNumber, token))
        : enduserApi.publicGet<DeliveryStatusResponse>(statusPath(referenceNumber, token)),
  });

  return {
    status: query.data ?? null,
    loading: query.isPending,
    error: query.isError
      ? query.error instanceof ApiError
        ? query.error.message
        : "Kunne ikke hente status"
      : null,
    refetch: () => query.refetch(),
    setCustomerComment: (customerComment: string | null) => {
      queryClient.setQueryData<DeliveryStatusResponse>(queryKey, (current) =>
        current ? { ...current, customerComment } : current,
      );
    },
  };
}
